import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';
import { ErrorBoundary } from '../lib/errors';
import { useAnalyticsSink } from '../lib/analytics';
import { useFeatureIsOn } from '../lib/flags';
import { BASENAME } from '../app/registry';
import { installKeyboardLift, settle } from './keyboardLift';
import { ErrorScreen } from './ErrorScreen';
import { OfflineNote } from './OfflineNote';
import { ConsentSlot } from './ConsentSlot';
import { TurnstileHost } from './TurnstileHost';
import { StageLoading } from '../app/lazyScreen';
import './chrome.css';

// Dev-only: never in the production bundle (import.meta.env.DEV is statically false there).
const DevDrawer = import.meta.env.DEV ? lazy(() => import('../dev/DevDrawer')) : null;

interface RouteHandle {
  screen?: string;
}

/**
 * The root layout: one centred 480 px column holding whichever screen is routed.
 * Owns what every screen shares: scroll-to-top on navigation, the keyboard lift,
 * screen_view events, the cookie banner, the error boundary, the Turnstile widget of the guarded
 * API calls and (in dev) the ⚙ drawer.
 */
export function Shell() {
  const location = useLocation();
  const matches = useMatches();
  const sink = useAnalyticsSink();
  const devDrawer = useFeatureIsOn('dev_drawer');
  const screen = (matches.at(-1)?.handle as RouteHandle | undefined)?.screen ?? 'unknown';

  useEffect(() => installKeyboardLift(), []);

  // Every navigation starts at the top, back/forward included (the prototype's render()).
  // Hash fragments are never used for screens, so the browser can't jump to an element id.
  useLayoutEffect(() => {
    settle();
    window.scrollTo(0, 0);
  }, [location.key]);

  // One screen_view per page: not again for a query change on the same path (S1 drops ?type=,
  // S3 adds ?leg=… on its own path) or StrictMode's second effect run in dev.
  const viewed = useRef<string | null>(null);
  useEffect(() => {
    if (viewed.current === location.pathname) return;
    viewed.current = location.pathname;
    sink.track({ name: 'screen_view', screen, path: `${BASENAME}${location.pathname}` });
  }, [sink, screen, location.pathname]);

  return (
    <div className="app">
      <ConsentSlot />
      <OfflineNote />
      <ErrorBoundary
        resetKey={location.pathname}
        fallback={(e, reset) => <ErrorScreen error={e} onReset={reset} />}
      >
        <Suspense fallback={<StageLoading />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
      <TurnstileHost />
      {DevDrawer && devDrawer && (
        <Suspense fallback={null}>
          <DevDrawer />
        </Suspense>
      )}
    </div>
  );
}
